import React, {
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useBonusTags from 'hooks/useBonusTags';
import { BonusOverviewContext } from '../../providers/BonusOverviewProvider';
import FilterPlatesProvider from '../../../../contextProviders/FilterPlatesProvider';
import useGetBonusRecordTaggingConfig from '../../../../screens/Settings/Advanced/BonusRecordTaggingFields/useGetBonusRecordTaggingConfig';
import FlexCentered from '../../../Common/FlexCentered';
import Loading from '../../../Loading';
import Typography from '../../../../kingpin/atoms/Typography';
import {
  DEFAULT_EXCLUDED_TAG,
  DEFAULT_EXCLUDED_TAG_OPTION,
} from './tagConstants';

type BonusRecordTaggingContextType = {
  isLoading: boolean;
  gridConfig: BonusRecordTagging.RecordTaggingConfig | undefined;
  isShowingAllFields: boolean;
  setIsShowingAllFields: (isShowing: boolean) => void;
  selectedDocumentIds: string[];
  setSelectedDocumentIds: React.Dispatch<SetStateAction<string[]>>;
  taggedDocumentIds: string[];
  setTaggedDocumentIds: React.Dispatch<SetStateAction<string[]>>;
  comment: string;
  setComment: (comment: string) => void;
  dateScopeOverride: DateRangeInput | undefined;
  setDateScopeOverride: React.Dispatch<
    React.SetStateAction<DateRangeInput | undefined>
  >;
  dateRangeWindow: DateRangeInput | undefined;
  isRecordTaggingFlowActive: boolean;
  // The tag this view writes/removes (falls back to EXCLUDED for legacy configs).
  configuredTag: string;
  // How to render the tag's pill in the grid.
  tagOption: BonusTags.TagOption;
  // Once the period is pending sign off or closed the data is locked — row
  // selection and the save/delete actions are disabled.
  isReadOnly: boolean;
};

export const BonusRecordTaggingContext =
  React.createContext<BonusRecordTaggingContextType>({
    isLoading: true,
    gridConfig: undefined,
    isShowingAllFields: false,
    taggedDocumentIds: [],
    setTaggedDocumentIds: () => {},
    selectedDocumentIds: [],
    setSelectedDocumentIds: () => {},
    setIsShowingAllFields: () => {},
    comment: '',
    setComment: () => {},
    dateScopeOverride: undefined,
    setDateScopeOverride: () => {},
    dateRangeWindow: undefined,
    isRecordTaggingFlowActive: false,
    configuredTag: DEFAULT_EXCLUDED_TAG,
    tagOption: DEFAULT_EXCLUDED_TAG_OPTION,
    isReadOnly: false,
  });

const BonusRecordTaggingProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { bonusOverview, currentMode } = useContext(BonusOverviewContext);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [gridConfig, setGridConfig] = useState<
    BonusRecordTagging.RecordTaggingConfig | undefined
  >();
  const [isShowingAllFields, setIsShowingAllFields] = useState(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [taggedDocumentIds, setTaggedDocumentIds] = useState<string[]>([]);
  const [comment, setComment] = useState<string>('');
  const [dateScopeOverride, setDateScopeOverride] = useState<
    DateRangeInput | undefined
  >({
    startDate: bonusOverview?.bonusPeriod?.startDate,
    endDate: bonusOverview?.bonusPeriod?.endDate,
  });
  const [dateRangeWindow] = useState<DateRangeInput | undefined>({
    startDate: bonusOverview?.bonusPeriod?.startDate,
    endDate: bonusOverview?.bonusPeriod?.endDate,
  });

  const getBonusRecordTaggingConfig = useGetBonusRecordTaggingConfig();

  const configuredTag = gridConfig?.tag || DEFAULT_EXCLUDED_TAG;
  const { tags: availableTags } = useBonusTags(gridConfig?.dataType);
  const tagOption = useMemo((): BonusTags.TagOption => {
    const match = availableTags.find((t) => t.tag === configuredTag);
    if (match) return match;
    if (configuredTag === DEFAULT_EXCLUDED_TAG) {
      return DEFAULT_EXCLUDED_TAG_OPTION;
    }
    // Tag configured but no matching SE definition (e.g. deleted) — still render
    // a readable pill.
    return { tag: configuredTag, label: configuredTag, color: 'Blue' };
  }, [availableTags, configuredTag]);

  useEffect(() => {
    if (currentMode.mode !== 'recordTagging') return;
    if (!currentMode.taggableField || !bonusOverview) return;

    const taggableField = currentMode.taggableField;

    getBonusRecordTaggingConfig({
      fieldType: currentMode.fieldType ?? 'criteria',
      fieldName: taggableField.name,
      bonusProgramId: bonusOverview.bonusProgram.id,
    })
      .then((config) => {
        setGridConfig(config);
        setIsConfigLoading(false);
      })
      .catch(() => {
        setConfigError(true);
        setIsConfigLoading(false);
      });
  }, [bonusOverview, currentMode, getBonusRecordTaggingConfig]);

  // Tag edits are only allowed while the period is still live ('active' or
  // 'in review'). Everything else — 'pending sign off', 'closed', 'archived', or
  // any future status — is read-only by default (fail closed).
  const periodStatus = bonusOverview?.bonusPeriod.status;
  const isReadOnly = periodStatus !== 'active' && periodStatus !== 'in review';

  if (isConfigLoading) {
    return (
      <FlexCentered>
        <Loading />
      </FlexCentered>
    );
  }

  if (configError) {
    return (
      <FlexCentered>
        <Typography.Header type={'H3'}>
          Failed to load data view configuration
        </Typography.Header>
      </FlexCentered>
    );
  }

  if (!gridConfig) {
    return (
      <FlexCentered>
        <Typography.Header type={'H3'}>
          No data view configured for this field
        </Typography.Header>
      </FlexCentered>
    );
  }

  return (
    <BonusRecordTaggingContext.Provider
      value={{
        dateScopeOverride,
        setDateScopeOverride,
        dateRangeWindow,
        isLoading: false,
        gridConfig,
        selectedDocumentIds,
        setSelectedDocumentIds,
        taggedDocumentIds,
        setTaggedDocumentIds,
        isShowingAllFields,
        setIsShowingAllFields,
        setComment,
        comment,
        isRecordTaggingFlowActive: true,
        configuredTag,
        tagOption,
        isReadOnly,
      }}
    >
      <FilterPlatesProvider scope={gridConfig.filters}>
        {children}
      </FilterPlatesProvider>
    </BonusRecordTaggingContext.Provider>
  );
};

export default BonusRecordTaggingProvider;
